/**
 * 巻き戻し用のセーブステート・リングバッファ。
 *
 * レトロゲームは即死が多く、巻き戻しがあるかどうかで遊びやすさが大きく変わる。
 * ただしステートのサイズは機種差が激しい:
 *
 *   GB    数十 KB
 *   NES   数十 KB
 *   SNES  数百 KB
 *   PS1   数 MB
 *
 * iPhone は 1 タブあたり実質 1GB 程度でクラッシュするので、
 * 「何秒ぶん」ではなく **バイト数の上限** で制御する。
 * 1 個が大きすぎる機種では自動的に無効化して、落ちるより諦める方を選ぶ。
 */

export interface RewindOptions {
  /** 何フレームおきに記録するか。60fps で 6 なら 1 秒に 10 個。 */
  intervalFrames?: number;
  /** バッファ全体の上限バイト数 */
  maxBytes?: number;
  /** 1 個がこのサイズを超える機種では巻き戻しを諦める */
  maxStateBytes?: number;
}

const DEFAULTS = {
  intervalFrames: 6,
  // 実測で iPhone でも安全に扱える範囲に抑えている
  maxBytes: 48 * 1024 * 1024,
  maxStateBytes: 4 * 1024 * 1024,
} as const;

export class RewindBuffer {
  private entries: Uint8Array[] = [];
  private bytes = 0;
  private frameCounter = 0;
  private opts: Required<RewindOptions>;
  private disabledReason: string | null = null;

  constructor(options: RewindOptions = {}) {
    this.opts = { ...DEFAULTS, ...options };
  }

  get length(): number {
    return this.entries.length;
  }

  get byteLength(): number {
    return this.bytes;
  }

  get enabled(): boolean {
    return this.disabledReason === null;
  }

  get disabled(): string | null {
    return this.disabledReason;
  }

  /** 記録できる秒数の目安（UI 表示用） */
  secondsBuffered(fps: number): number {
    return (this.entries.length * this.opts.intervalFrames) / fps;
  }

  /**
   * フレーム毎に呼ぶ。間引いて記録する。
   * @param capture 実際に必要になったときだけステートを取るためのコールバック
   * @returns 記録したら true
   */
  tick(capture: () => Uint8Array): boolean {
    if (this.disabledReason) return false;

    this.frameCounter++;
    if (this.frameCounter < this.opts.intervalFrames) return false;
    this.frameCounter = 0;

    let state: Uint8Array;
    try {
      state = capture();
    } catch (e) {
      // ステート非対応のコアでは巻き戻しもできない
      this.disable(e instanceof Error ? e.message : 'セーブステートに対応していません');
      return false;
    }

    if (state.length > this.opts.maxStateBytes) {
      this.disable(
        `セーブステートが大きすぎます (${Math.round(state.length / 1024 / 1024)}MB)。この機種では巻き戻しを無効にします。`,
      );
      return false;
    }

    this.entries.push(state);
    this.bytes += state.length;
    this.evict();
    return true;
  }

  /** 上限を超えた分を古い方から捨てる */
  private evict(): void {
    while (this.bytes > this.opts.maxBytes && this.entries.length > 1) {
      const dropped = this.entries.shift();
      if (dropped) this.bytes -= dropped.length;
    }
  }

  /**
   * 1 つ前の状態を取り出す。
   * 最後の 1 個は残す（巻き戻しきったあとも復帰できるように）。
   */
  pop(): Uint8Array | null {
    if (this.entries.length === 0) return null;
    if (this.entries.length === 1) return this.entries[0] ?? null;

    const state = this.entries.pop();
    if (!state) return null;
    this.bytes -= state.length;
    return state;
  }

  /** 直近の状態を消さずに見る */
  peek(): Uint8Array | null {
    return this.entries.at(-1) ?? null;
  }

  clear(): void {
    this.entries = [];
    this.bytes = 0;
    this.frameCounter = 0;
  }

  /** ステート読み込みなどで時系列が飛んだときに呼ぶ */
  reset(): void {
    this.clear();
  }

  private disable(reason: string): void {
    this.disabledReason = reason;
    this.clear();
  }

  /** 機種を変えて作り直すときに、無効化状態も含めて初期化する */
  reenable(): void {
    this.disabledReason = null;
    this.clear();
  }
}
